import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styles from './BusinessReviewsPage.module.css';
import { API_BASE_URL } from '../../config';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

// Components
import ModernNavbarPureCSS from "../../components/Navbar/ModernNavBarPureCSS";
import HomeUserProfilePureCSS from "../../profiles/HomeProfilePureCSS";
import BusinessProfilePureCSS from "../../profiles/BusinessProfilePureCSS"; // Added Import
import { Separator } from "../../_newprofiles/components/ui/Separator"

// Icons
import { Star, MessageSquare } from 'lucide-react';

const BusinessReviewsPage = () => {
    const [reviews, setReviews] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [username, setUsername] = useState('');
    
    // Image Modal State
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const [reviewImages, setReviewImages] = useState([]);
    
    // Profile Modal State
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [selectedUserType, setSelectedUserType] = useState(null); // Added to track type


    // NEW STATE FOR TABS
    // Options: 'received' (default), 'written'
    const [activeTab, setActiveTab] = useState('received'); 
    
    // Store Identity details to reuse in fetch
    const [identity, setIdentity] = useState({
        userType: '',
        businessId: null, // For "Received"
        userId: null      // For "Written" (BusinessUserId or HomeUserId)
    });


    const openImageModal = (images, index) => {
        setReviewImages(images.filter(img => img)); 
        setSelectedImageIndex(index);
        setIsImageModalOpen(true);
    };

    const closeImageModal = () => {
        setIsImageModalOpen(false);
    };

    const nextImage = () => {
        setSelectedImageIndex((prevIndex) => (prevIndex + 1) % reviewImages.length);
    };

    const prevImage = () => {
        setSelectedImageIndex((prevIndex) => (prevIndex - 1 + reviewImages.length) % reviewImages.length);
    };

    // Updated to accept UserType
    const handleOpenProfile = (userId, userType) => {
        setSelectedUserId(userId);
        setSelectedUserType(userType);
        setIsProfileOpen(true);
    };

    const handleCloseProfile = () => {
        setIsProfileOpen(false);
        setSelectedUserId(null);
        setSelectedUserType(null);
    };

    const getInitials = (firstName, lastName) => {
        const firstInitial = firstName ? firstName.charAt(0) : '';
        const lastInitial = lastName ? lastName.charAt(0) : '';
        return `${firstInitial}${lastInitial}`.toUpperCase();
    };

    const pastelColors = [
        '#ff5050', '#9999ff', '#00cc66', '#6699ff', '#33cccc', '#8585ad', '#669999', '#ffb84d'
    ];

    const getColorForUser = (userId) => {
        return pastelColors[userId % pastelColors.length];
    };

    const timeSince = (date) => {
        const seconds = Math.floor((new Date() - new Date(date)) / 1000);
        let interval = seconds / 31536000;
        if (interval > 1) return Math.floor(interval) + " years ago";
        interval = seconds / 2592000;
        if (interval > 1) return Math.floor(interval) + " months ago";
        interval = seconds / 86400;
        if (interval > 1) return Math.floor(interval) + " days ago";
        interval = seconds / 3600;
        if (interval > 1) return Math.floor(interval) + " hours ago";
        interval = seconds / 60;
        if (interval > 1) return Math.floor(interval) + " minutes ago";
        return Math.floor(seconds) + " seconds ago";
    };

    useEffect(() => {
        const token = localStorage.getItem('token');
        let businessId = null;
        let loggedInHomeUserId = localStorage.getItem('loggedInHomeUserId');
        let userType = "";
        let reviewsURL = "";

        if (token) {
            const decodeToken = jwtDecode(token);
            userType = decodeToken.UserType;
            setUsername(decodeToken.UserName || 'User');
            businessId = decodeToken.BusinessId;

            if (userType === 'Business') {
                businessId = decodeToken.BusinessId;
            } else {
                loggedInHomeUserId = localStorage.getItem('loggedInHomeUserId');
            }
        }

        const fetchReviews = async () => {
            if (userType == 'Business') {
                if (!businessId) {
                    setError("Business ID not found. Please log in as a business user.");
                    setIsLoading(false);
                    return;
                }
                reviewsURL = `${API_BASE_URL}/api/BusinessReview/getReviewsByBusinessId?businessId=${businessId}&baseUrl=base`;
            } else {
                if (!loggedInHomeUserId) {
                    setError("HomeUser ID not found. Please log in as a Home User.");
                    setIsLoading(false);
                    return;
                }
                reviewsURL = `${API_BASE_URL}/api/BusinessReview/getReviewsByUserId?userId=${loggedInHomeUserId}&baseUrl=base`;
            }

            setIsLoading(true);
            try {
                const response = await axios.get(reviewsURL);
                if (response.data && Array.isArray(response.data.model)) {
                    setReviews(response.data.model);
                } else {
                    setReviews([]);
                }
            } catch (err) {
                setError('Failed to fetch reviews. Please try again.');
                console.error(err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchReviews();
    }, []);


    useEffect(() => {
        const token = localStorage.getItem('token');
        let decodedIdentity = { userType: '', businessId: null, userId: null };

        if (token) {
            const decodeToken = jwtDecode(token);
            // Ensure we handle 'Business' vs 'BusinessUser' consistency
            const rawUserType = decodeToken.UserType; 
            
            decodedIdentity.userType = rawUserType;
            decodedIdentity.businessId = decodeToken.BusinessId;
            
            if (rawUserType === 'Business') {
                // IMPORTANT: For a business writing a review, we need their User Table ID (Id), not BusinessId.
                // Assuming your JWT has the specific table ID, often mapped as NameIdentifier or a custom claim.
                // If not, you might need to use BusinessId, but usually tables are joined on UserID.
                decodedIdentity.userId = decodeToken.Id || decodeToken.UserId; 
            } else {
                decodedIdentity.userId = localStorage.getItem('loggedInHomeUserId');
                // Home users only have "written" view, effectively
                setActiveTab('written'); 
            }
        }
        setIdentity(decodedIdentity);
    }, []);

    // Effect to fetch reviews whenever ActiveTab or Identity changes
    useEffect(() => {
        if (!identity.userType) return;

        const fetchReviews = async () => {
            setIsLoading(true);
            setError(null);
            let reviewsURL = "";

            try {
                // LOGIC SPLIT BASED ON TAB
                if (identity.userType === 'Business') {
                    if (activeTab === 'received') {
                         // 1. Business: Reviews Received (Old Logic)
                        if (!identity.businessId) throw new Error("Business ID missing");
                        reviewsURL = `${API_BASE_URL}/api/BusinessReview/getReviewsByBusinessId?businessId=${identity.businessId}&baseUrl=base`;
                    } else {
                        // 2. Business: Reviews Written (New Logic)
                        // We pass 'BusinessUser' as userType so backend checks the right table
                        reviewsURL = `${API_BASE_URL}/api/BusinessReview/getReviewsByUserId?userId=${identity.businessId}&baseUrl=base&userType=BusinessUser`;
                    }
                } else {
                    // 3. Home User: Reviews Written (Old Logic)
                    if (!identity.userId) throw new Error("HomeUser ID missing");
                    reviewsURL = `${API_BASE_URL}/api/BusinessReview/getReviewsByUserId?userId=${identity.userId}&baseUrl=base&userType=HomeUser`;
                }

                const response = await axios.get(reviewsURL);
                if (response.data && Array.isArray(response.data.model)) {
                    setReviews(response.data.model);
                } else {
                    setReviews([]);
                }
            } catch (err) {
                // Don't show error if it's just "No reviews found" (handled by empty list UI)
                console.error(err);
                if(err.message !== "Business ID missing") setError('Failed to fetch reviews.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchReviews();
    }, [activeTab, identity]); // Re-run when tab switches




    return (
        <div className={styles.pageContainer}>
            
            <ModernNavbarPureCSS UserName={username} prefix={''} TabActiveId={'reviews'} />

            {/* GOLD HEADER BAR */}
            <header className={styles.header}>
                <div className={styles.headerContent}>
                    <div>
                        <h1 className={styles.headerTitle}>Business Reviews</h1>
                    </div>
                </div>
            </header>

            <div className={styles.mainContainer}>


                {/* --- TABS SECTION (Only for Business Users) --- */}
                {identity.userType === 'Business' && (
                    <div className={styles.consoleTabs}>
                        <button 
                            onClick={() => setActiveTab('received')} 
                            className={activeTab === 'received' ? styles.tabActive : styles.tabInactive}
                        >
                            Reviews Received
                        </button>
                        <button 
                            onClick={() => setActiveTab('written')} 
                            className={activeTab === 'written' ? styles.tabActive : styles.tabInactive}
                        >
                            Reviews Written
                        </button>
                    </div>
                )}




                {isLoading && <p>Loading reviews...</p>}
                {error && <p className={styles.error}>{error}</p>}

                {!isLoading && reviews.length === 0 && (
                    <div style={{textAlign:'center', padding:'4rem', color:'#64748b', border:'2px dashed #e2e8f0', borderRadius:'12px'}}>
                        <MessageSquare size={48} style={{opacity:0.2, marginBottom:'1rem'}} />
                        <p>
                            {activeTab === 'received' 
                                ? "No reviews received yet." 
                                : "You haven't written any reviews yet."}
                        </p>
                    </div>
                )}



                {!isLoading && reviews.length > 0 && (
                    <div className={styles.reviewList}>
                        {reviews.map((review) => (
                            <div key={review.reviewId} className={styles.reviewCard}>
                                <div className={styles.reviewCardHeader}>
                                    <div className={styles.profilePic} style={{ backgroundColor: getColorForUser(review.userId) }}>
                                        {getInitials(review.firstName, review.lastName)}
                                    </div>
                                    <div className={styles.reviewCardHeaderInfo}>
                                        
                                        {/* Logic Change: Who is the name linking to? */}
                                        {/* If looking at 'Received': Link to the person who wrote it */}
                                        {/* If looking at 'Written': Link to myself? Or maybe disable link? */}
                                        
                                        <button 
                                            onClick={() => handleOpenProfile(review.userId, review.userType)} 
                                            className={styles.clientNameLink}
                                        >
                                            {`${review.firstName} ${review.lastName}`}
                                        </button>
                                        
                                        <span className={styles.reviewBusinessName}>
                                            {/* Visual helper to clarify context */}
                                            {activeTab === 'written' ? 'You reviewed: ' : 'Review for: '} 
                                            <strong>{review.businessName}</strong>
                                        </span>
                                    </div>
                                </div>
                                
                                {/* ... Rest of the card (Stars, Description, Images) stays the same ... */}
                                <div className={styles.reviewCardRating}>
                                    {Array(5).fill(0).map((_, i) => (
                                        <Star key={i} size={16} fill={i < review.overallRating ? "#f59e0b" : "none"} stroke={i < review.overallRating ? "#f59e0b" : "#cbd5e1"} />
                                    ))}
                                    <span className={styles.reviewDate}>{timeSince(review.dateAdded)}</span>
                                </div>
                                
                                <p className={styles.reviewDescription}>{review.description}</p>
                                
                                {review.mediaUrl && review.mediaUrl.length > 0 && (
                                    <>
                                        <Separator className="my-4" />
                                        <div className={styles.projectImagesGrid}>
                                            {review.mediaUrl.map((media, index) => (
                                                media.url && <img 
                                                    key={index} 
                                                    src={media.url} 
                                                    alt={`Review image`} 
                                                    className={styles.projectImage} 
                                                    onClick={() => openImageModal(review.mediaUrl.map(m => m.url), index)} 
                                                />
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {isImageModalOpen && (
                <div className={styles.imageModalOverlay} onClick={closeImageModal}>
                    <div className={styles.imageModalContent} onClick={(e) => e.stopPropagation()}>
                        <button className={styles.closeButton} onClick={closeImageModal}>&times;</button>
                        <button className={styles.prevButton} onClick={prevImage}>&#10094;</button>
                        <img src={reviewImages[selectedImageIndex]} alt="Enlarged review" className={styles.enlargedImage} />
                        <button className={styles.nextButton} onClick={nextImage}>&#10095;</button>
                    </div>
                </div>
            )}
            
            {/* Conditional Profile Rendering */}
            
            {/* Condition: Open AND (UserType is 'HomeUser' OR UserType is null/empty/undefined) */}
            {isProfileOpen && selectedUserType !== 'BusinessUser' && (
                <HomeUserProfilePureCSS 
                    isOpen={true} 
                    onClose={handleCloseProfile} 
                    userId={selectedUserId}
                    showMessageButton={false} 
                />
            )}

            {/* 2. Business User Profile */}
            {/* Condition: Open AND UserType IS STRICTLY 'BusinessUser' */}
            {isProfileOpen && selectedUserType === 'BusinessUser' && (
                <BusinessProfilePureCSS 
                    isOpen={true} 
                    onClose={handleCloseProfile} 
                    businessId={selectedUserId} 
                    useIDTYPE={"BID"}
                    showMessageButton={false}
                />
            )}

        </div>
    );
};

export default BusinessReviewsPage;